import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { cn } from '@/lib/cn'
import {
  Search, Sparkles, Send, Paperclip, Smile, Phone, Info,
  CheckCheck, X, Tag, ShoppingBag, StickyNote, MoreVertical,
  Bot, Plus, Mic, Filter, Clock, ChevronRight, RefreshCw,
} from 'lucide-react'
import { Badge, Input, Button } from '@/components/ui'

export const Route = createFileRoute('/store/$id/whatsapp')({
  component: WhatsAppCRM,
})

// ─── Types ────────────────────────────────────────────────────────────────────

interface Contact {
  id: string
  name: string
  phone: string
  initials: string
  color: string
  status: 'online' | 'offline' | 'typing'
  lastMessage: string
  lastTime: string
  unread: number
  aiEnabled: boolean
  tags: string[]
  orders: number
  note: string
}

interface Message {
  id: string
  from: 'contact' | 'agent' | 'ai'
  text: string
  time: string
}

interface DayMessages {
  date: string
  messages: Message[]
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const CONTACTS: Contact[] = [
  {
    id: '1', name: 'María González', phone: '+54 9 11 2345-6789',
    initials: 'MG', color: 'bg-purple-600',
    status: 'typing', lastMessage: 'Tiene el talle M en stock?', lastTime: '10:42',
    unread: 3, aiEnabled: true,
    tags: ['cliente', 'vip'], orders: 4,
    note: 'Cliente frecuente, prefiere talles M. Consulta mucho por novedades.',
  },
  {
    id: '2', name: 'Carlos Mendoza', phone: '+54 9 351 8765-4321',
    initials: 'CM', color: 'bg-blue-600',
    status: 'online', lastMessage: 'Gracias! Ya quedó el pedido', lastTime: '09:15',
    unread: 0, aiEnabled: true,
    tags: ['cliente'], orders: 2,
    note: 'Prefiere ser contactado por la mañana.',
  },
  {
    id: '3', name: 'Laura Fernández', phone: '+54 9 11 9999-1234',
    initials: 'LF', color: 'bg-rose-600',
    status: 'offline', lastMessage: 'Cuánto tarda el envío a Rosario?', lastTime: 'Ayer',
    unread: 1, aiEnabled: false,
    tags: ['potencial'], orders: 0,
    note: 'Interesada en envíos al interior. Sin compras todavía.',
  },
  {
    id: '4', name: 'Diego Ramírez', phone: '+54 9 11 5555-7890',
    initials: 'DR', color: 'bg-amber-600',
    status: 'offline', lastMessage: 'Ok, voy a pensarlo y te aviso', lastTime: 'Ayer',
    unread: 0, aiEnabled: false,
    tags: ['potencial'], orders: 0,
    note: '',
  },
  {
    id: '5', name: 'Ana Martínez', phone: '+54 9 261 3333-0001',
    initials: 'AM', color: 'bg-teal-600',
    status: 'online', lastMessage: 'Me llegó con un problema, puede ser?', lastTime: 'Mar',
    unread: 0, aiEnabled: true,
    tags: ['cliente', 'soporte'], orders: 7,
    note: 'Comprador frecuente. Reportó problema con último envío.',
  },
  {
    id: '6', name: 'Roberto Silva', phone: '+54 9 11 7777-2222',
    initials: 'RS', color: 'bg-indigo-600',
    status: 'offline', lastMessage: 'Hola! Me interesa el producto', lastTime: 'Lun',
    unread: 0, aiEnabled: true,
    tags: ['nuevo'], orders: 0,
    note: '',
  },
  {
    id: '7', name: 'Valentina López', phone: '+54 9 11 4444-3333',
    initials: 'VL', color: 'bg-pink-600',
    status: 'offline', lastMessage: 'Perfecto, muchas gracias!', lastTime: 'Dom',
    unread: 0, aiEnabled: false,
    tags: ['cliente'], orders: 1,
    note: 'Primera compra completada con éxito.',
  },
]

const CHAT_MESSAGES: DayMessages[] = [
  {
    date: 'Ayer',
    messages: [
      { id: 'm1', from: 'contact', text: 'Hola! Vi su producto en Instagram y me interesó mucho 👀', time: '18:30' },
      { id: 'm2', from: 'ai', text: '¡Hola María! Qué bueno que te haya llamado la atención 😊 ¿Cuál de nuestros productos te interesó? Puedo darte todos los detalles: disponibilidad, tallas y precio.', time: '18:30' },
      { id: 'm3', from: 'contact', text: 'La remera oversize negra que publicaron ayer', time: '18:32' },
      { id: 'm4', from: 'ai', text: 'Excelente elección! La Remera Oversize Essential en negro es uno de nuestros favoritos 🖤 Tenemos disponibilidad en talles XS, S, M, L y XL. El precio es $15.990. ¿Te gustaría hacer un pedido?', time: '18:32' },
      { id: 'm5', from: 'contact', text: 'Y cuánto tarda el envío si soy de CABA?', time: '18:35' },
      { id: 'm6', from: 'ai', text: 'Para CABA el envío llega en 24-48hs hábiles vía Andreani 📦 El costo es $890, pero es gratis en compras mayores a $25.000. ¿Arrancamos con el pedido?', time: '18:36' },
    ],
  },
  {
    date: 'Hoy',
    messages: [
      { id: 'm7', from: 'contact', text: 'Buenos días! Quería confirmar si sigue disponible la remera', time: '10:15' },
      { id: 'm8', from: 'ai', text: '¡Buenos días, María! Sí, sigue disponible 🎉 ¿Procedemos con el pedido? Solo me decís el talle y la dirección de envío y lo dejamos listo.', time: '10:15' },
      { id: 'm9', from: 'contact', text: 'Tiene el talle M en stock?', time: '10:42' },
    ],
  },
]

const AI_SUGGESTION = '¡Hola María! Sí, tenemos talle M disponible en este momento. ¿Lo reservamos para vos? Solo necesito confirmar la dirección de envío 😊'

const QUICK_REPLIES = [
  'Sí, tenemos stock disponible ✅',
  'El envío demora entre 24 y 48hs hábiles',
  'Podés pagar con tarjeta, transferencia o Mercado Pago',
  'Te paso el link para hacer el pedido',
]

// ─── Main component ───────────────────────────────────────────────────────────

function WhatsAppCRM() {
  const [selectedId, setSelectedId] = useState('1')
  const [aiStates, setAiStates] = useState<Record<string, boolean>>(
    Object.fromEntries(CONTACTS.map((c) => [c.id, c.aiEnabled])),
  )
  const [tab, setTab] = useState<'all' | 'unread' | 'ai'>('all')
  const [search, setSearch] = useState('')
  const [message, setMessage] = useState('')
  const [showInfo, setShowInfo] = useState(true)
  const [showQuickReplies, setShowQuickReplies] = useState(false)
  const [showSuggestion, setShowSuggestion] = useState(true)

  const contact = CONTACTS.find((c) => c.id === selectedId) ?? CONTACTS[0]
  const aiOn = aiStates[contact.id] ?? false

  function toggleAi(id: string) {
    setAiStates((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const filtered = CONTACTS.filter((c) => {
    if (tab === 'unread') return c.unread > 0
    if (tab === 'ai') return aiStates[c.id]
    return true
  }).filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.includes(search),
  )

  const totalUnread = CONTACTS.reduce((sum, c) => sum + c.unread, 0)
  const totalAiActive = Object.values(aiStates).filter(Boolean).length

  return (
    <div className="flex bg-background overflow-hidden" style={{ height: 'calc(100vh - 44px)' }}>

      {/* ── Panel izquierdo: lista de chats ────────────────────────────────── */}
      <div className="w-[300px] shrink-0 flex flex-col border-r border-border bg-[hsl(var(--surface))]">

        {/* Encabezado */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="size-7 rounded-lg bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center">
                <Bot className="size-4 text-emerald-700 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-sm font-semibold leading-none">WhatsApp CRM</h2>
                <p className="text-[10px] text-muted-foreground mt-0.5">{CONTACTS.length} contactos</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button className="size-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition">
                <Filter className="size-3.5" />
              </button>
              <button className="size-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition">
                <Plus className="size-3.5" />
              </button>
            </div>
          </div>

          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 size-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="pl-8 h-8 text-xs bg-[hsl(var(--muted))]/80 border-border/60 focus:ring-1"
            />
          </div>

          {/* Tabs */}
          <div className="flex rounded-md bg-[hsl(var(--muted))] p-0.5 gap-0.5">
            {([
              ['all', 'Todos'],
              ['unread', `Sin leer${totalUnread > 0 ? ` (${totalUnread})` : ''}`],
              ['ai', 'IA activa'],
            ] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={cn(
                  'flex-1 text-[10px] font-medium py-1 rounded transition',
                  tab === key
                    ? 'bg-emerald-600/25 text-emerald-700 dark:text-emerald-300'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Lista de contactos */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm text-muted-foreground">Sin resultados</p>
            </div>
          )}
          {filtered.map((c) => {
            const isSelected = c.id === selectedId
            const isAiOn = aiStates[c.id]
            return (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                className={cn(
                  'w-full flex items-start gap-3 px-4 py-3 text-left transition border-b border-border/40',
                  isSelected
                    ? 'bg-[hsl(var(--muted))]/70 border-l-2 border-l-emerald-500'
                    : 'hover:bg-[hsl(var(--muted))]/30 border-l-2 border-l-transparent',
                )}
              >
                {/* Avatar */}
                <div className="relative shrink-0 mt-0.5">
                  <div className={cn('size-9 rounded-full flex items-center justify-center text-xs font-bold text-foreground', c.color)}>
                    {c.initials}
                  </div>
                  <span className={cn(
                    'absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-border',
                    c.status === 'online' ? 'bg-emerald-400' :
                    c.status === 'typing' ? 'bg-blue-400 animate-pulse' :
                    'bg-muted-foreground',
                  )} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className={cn('text-xs font-semibold truncate', c.unread > 0 ? 'text-foreground' : 'text-foreground')}>
                      {c.name}
                    </span>
                    <span className="text-[9px] text-muted-foreground shrink-0 ml-1">{c.lastTime}</span>
                  </div>
                  <div className="flex items-center justify-between gap-1">
                    <p className={cn(
                      'text-[11px] truncate',
                      c.status === 'typing' ? 'text-blue-700 dark:text-blue-400 italic' :
                      c.unread > 0 ? 'text-foreground' : 'text-muted-foreground',
                    )}>
                      {c.status === 'typing' ? 'escribiendo...' : c.lastMessage}
                    </p>
                    <div className="flex items-center gap-1 shrink-0">
                      {isAiOn && <Sparkles className="size-3 text-emerald-700 dark:text-emerald-400" />}
                      {c.unread > 0 && (
                        <span className="min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-foreground text-[9px] font-bold flex items-center justify-center">
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {/* Stats footer */}
        <div className="p-3 border-t border-border grid grid-cols-3 gap-1.5">
          <div className="rounded-md bg-[hsl(var(--muted))]/60 py-2 text-center">
            <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">{totalAiActive}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">IA activa</p>
          </div>
          <div className="rounded-md bg-[hsl(var(--muted))]/60 py-2 text-center">
            <p className="text-sm font-bold text-amber-700 dark:text-amber-400">{totalUnread}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Sin leer</p>
          </div>
          <div className="rounded-md bg-[hsl(var(--muted))]/60 py-2 text-center">
            <p className="text-sm font-bold text-foreground">{CONTACTS.length}</p>
            <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">Chats</p>
          </div>
        </div>
      </div>

      {/* ── Panel central: ventana de chat ────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Header del chat */}
        <div className="h-14 px-4 border-b border-border flex items-center justify-between gap-3 shrink-0 bg-[hsl(var(--surface))]/20">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className={cn('size-8 rounded-full flex items-center justify-center text-xs font-bold text-foreground shrink-0', contact.color)}>
                {contact.initials}
              </div>
              <span className={cn(
                'absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-border',
                contact.status === 'online' ? 'bg-emerald-400' :
                contact.status === 'typing' ? 'bg-blue-400 animate-pulse' :
                'bg-muted-foreground',
              )} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">{contact.name}</h3>
                {contact.tags.map((t) => (
                  <Badge key={t} variant="muted" className="text-[9px] px-1.5 py-0">{t}</Badge>
                ))}
              </div>
              <p className={cn(
                'text-[10px]',
                contact.status === 'typing' ? 'text-blue-700 dark:text-blue-400 italic' :
                contact.status === 'online' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground',
              )}>
                {contact.status === 'typing' ? 'escribiendo...' :
                 contact.status === 'online' ? 'en línea' : contact.phone}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle IA */}
            <button
              onClick={() => toggleAi(contact.id)}
              className={cn(
                'flex items-center gap-2 px-3 h-7 rounded-full border text-xs font-medium transition-all',
                aiOn
                  ? 'bg-emerald-100 dark:bg-emerald-500/10 border-emerald-600/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20'
                  : 'bg-[hsl(var(--muted))] border-border text-muted-foreground hover:text-foreground',
              )}
            >
              <Sparkles className="size-3" />
              <span>{aiOn ? 'IA activa' : 'IA inactiva'}</span>
              {/* Switch visual */}
              <span className={cn(
                'relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors',
                aiOn ? 'bg-emerald-500' : 'bg-muted-foreground',
              )}>
                <span className={cn(
                  'absolute inline-block h-2.5 w-2.5 rounded-full bg-white shadow transition-transform',
                  aiOn ? 'translate-x-[11px]' : 'translate-x-0.5',
                )} />
              </span>
            </button>

            <button className="size-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition">
              <Phone className="size-3.5" />
            </button>
            <button
              onClick={() => setShowInfo((v) => !v)}
              className={cn(
                'size-7 flex items-center justify-center rounded-md transition',
                showInfo ? 'bg-[hsl(var(--muted))] text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]',
              )}
            >
              <Info className="size-3.5" />
            </button>
            <button className="size-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition">
              <MoreVertical className="size-3.5" />
            </button>
          </div>
        </div>

        {/* Mensajes */}
        <div
          className="flex-1 overflow-y-auto px-5 py-4 space-y-0.5"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.02) 1px, transparent 0)',
            backgroundSize: '20px 20px',
          }}
        >
          {CHAT_MESSAGES.map((day) => (
            <div key={day.date}>
              {/* Separador de fecha */}
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-[hsl(var(--muted))]/70" />
                <span className="text-[9px] text-muted-foreground font-medium px-2.5 py-0.5 rounded-full bg-[hsl(var(--muted))]/60 border border-border">
                  {day.date}
                </span>
                <div className="flex-1 h-px bg-[hsl(var(--muted))]/70" />
              </div>

              {day.messages.map((msg) => {
                const isIncoming = msg.from === 'contact'
                const isAi = msg.from === 'ai'

                return (
                  <div
                    key={msg.id}
                    className={cn('flex mb-2', isIncoming ? 'justify-start' : 'justify-end')}
                  >
                    <div className={cn(
                      'max-w-[72%] rounded-2xl px-3.5 py-2.5',
                      isIncoming
                        ? 'bg-[hsl(var(--muted))] text-foreground rounded-tl-sm'
                        : isAi
                          ? 'bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800/40 text-foreground rounded-tr-sm'
                          : 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-tr-sm',
                    )}>
                      {isAi && (
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Sparkles className="size-3 text-emerald-700 dark:text-emerald-400" />
                          <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold tracking-wide uppercase">Respuesta IA</span>
                        </div>
                      )}
                      <p className="text-sm leading-relaxed">{msg.text}</p>
                      <div className={cn('flex items-center gap-1 mt-1.5', isIncoming ? 'justify-start' : 'justify-end')}>
                        <span className="text-[9px] text-muted-foreground">{msg.time}</span>
                        {!isIncoming && <CheckCheck className="size-3 text-emerald-700 dark:text-emerald-400" />}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ))}

          {/* Indicador de escritura */}
          {contact.status === 'typing' && (
            <div className="flex justify-start mb-2">
              <div className="bg-[hsl(var(--muted))] rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                <span className="size-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="size-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '160ms' }} />
                <span className="size-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '320ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Sugerencia IA */}
        {aiOn && showSuggestion && (
          <div className="mx-4 mb-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/30 px-3 py-2.5 flex items-start gap-2.5">
            <Sparkles className="size-3.5 text-emerald-700 dark:text-emerald-400 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-emerald-500 font-semibold mb-0.5">Sugerencia de respuesta</p>
              <p className="text-xs text-foreground leading-relaxed">{AI_SUGGESTION}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0 mt-0.5">
              <button
                onClick={() => { setMessage(AI_SUGGESTION); setShowSuggestion(false) }}
                className="text-[10px] text-emerald-700 dark:text-emerald-400 hover:text-emerald-300 font-semibold hover:underline px-1"
              >
                Usar
              </button>
              <button
                onClick={() => setShowSuggestion(false)}
                className="text-muted-foreground hover:text-muted-foreground"
              >
                <X className="size-3" />
              </button>
            </div>
          </div>
        )}

        {/* Respuestas rápidas */}
        {showQuickReplies && (
          <div className="mx-4 mb-2 rounded-xl border border-border bg-[hsl(var(--surface))] overflow-hidden">
            <div className="px-3 py-2 border-b border-border flex items-center justify-between">
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Respuestas rápidas</span>
              <button onClick={() => setShowQuickReplies(false)} className="text-muted-foreground hover:text-foreground">
                <X className="size-3.5" />
              </button>
            </div>
            {QUICK_REPLIES.map((reply, i) => (
              <button
                key={i}
                onClick={() => { setMessage(reply); setShowQuickReplies(false) }}
                className="w-full text-left px-3 py-2 text-xs text-foreground hover:bg-[hsl(var(--muted))] border-b border-border last:border-0 transition flex items-center justify-between gap-2"
              >
                <span>{reply}</span>
                <ChevronRight className="size-3 text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
        )}

        {/* Input de mensaje */}
        <div className="px-4 py-3 border-t border-border flex items-end gap-2">
          <button
            onClick={() => setShowQuickReplies((v) => !v)}
            className={cn(
              'size-9 flex items-center justify-center rounded-md transition shrink-0 mb-0.5',
              showQuickReplies ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]',
            )}
            title="Respuestas rápidas"
          >
            <RefreshCw className="size-4" />
          </button>
          <button className="size-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition shrink-0 mb-0.5">
            <Smile className="size-4" />
          </button>
          <button className="size-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] transition shrink-0 mb-0.5">
            <Paperclip className="size-4" />
          </button>
          <div className="flex-1">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Escribir mensaje..."
              rows={1}
              className="w-full bg-[hsl(var(--muted))] border border-border rounded-2xl px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] resize-none"
              style={{ minHeight: '38px', maxHeight: '120px' }}
            />
          </div>
          <button
            className={cn(
              'size-9 rounded-full flex items-center justify-center transition shrink-0 mb-0.5',
              message.trim()
                ? 'bg-emerald-600 text-foreground hover:bg-emerald-500'
                : 'bg-[hsl(var(--muted))] text-muted-foreground hover:bg-[hsl(var(--muted))] hover:text-foreground',
            )}
          >
            {message.trim() ? <Send className="size-4" /> : <Mic className="size-4" />}
          </button>
        </div>
      </div>

      {/* ── Panel derecho: info del contacto ──────────────────────────────── */}
      {showInfo && (
        <div className="w-60 shrink-0 border-l border-border bg-[hsl(var(--surface))] flex flex-col overflow-y-auto">

          {/* Perfil */}
          <div className="p-5 border-b border-border text-center">
            <div className={cn('size-14 rounded-full mx-auto flex items-center justify-center text-lg font-bold text-foreground mb-3', contact.color)}>
              {contact.initials}
            </div>
            <h3 className="font-semibold text-sm">{contact.name}</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">{contact.phone}</p>
            <div className="flex items-center justify-center gap-1 mt-2.5 flex-wrap">
              {contact.tags.map((t) => (
                <Badge key={t} variant="muted" className="text-[9px] px-1.5">{t}</Badge>
              ))}
            </div>
            {/* IA toggle compacto */}
            <button
              onClick={() => toggleAi(contact.id)}
              className={cn(
                'mt-3 w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border text-xs transition',
                aiStates[contact.id]
                  ? 'bg-emerald-100 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-700/50 text-emerald-700 dark:text-emerald-400'
                  : 'bg-[hsl(var(--muted))]/60 border-border text-muted-foreground',
              )}
            >
              <Sparkles className="size-3" />
              {aiStates[contact.id] ? 'IA habilitada' : 'IA deshabilitada'}
            </button>
          </div>

          {/* Pedidos */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                <ShoppingBag className="size-3" />
                Pedidos
              </div>
              <span className="text-xs text-muted-foreground font-medium">{contact.orders}</span>
            </div>
            {contact.orders > 0 ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between p-2 rounded-lg bg-[hsl(var(--muted))] text-xs">
                  <div>
                    <p className="font-semibold text-foreground">#ORD-1042</p>
                    <p className="text-muted-foreground text-[10px] mt-0.5">Remera Oversize · M</p>
                  </div>
                  <Badge variant="success" className="text-[9px]">Entregado</Badge>
                </div>
                {contact.orders > 1 && (
                  <div className="flex items-center justify-between p-2 rounded-lg bg-[hsl(var(--muted))] text-xs">
                    <div>
                      <p className="font-semibold text-foreground">#ORD-0987</p>
                      <p className="text-muted-foreground text-[10px] mt-0.5">Pantalón · 38</p>
                    </div>
                    <Badge variant="warning" className="text-[9px]">En camino</Badge>
                  </div>
                )}
                {contact.orders > 2 && (
                  <button className="w-full text-[10px] text-muted-foreground hover:text-foreground text-center py-1 hover:underline">
                    Ver {contact.orders - 2} pedido{contact.orders - 2 > 1 ? 's' : ''} más →
                  </button>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground text-center py-3">Sin pedidos todavía</p>
            )}
          </div>

          {/* Nota */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              <StickyNote className="size-3" />
              Nota interna
            </div>
            {contact.note ? (
              <div className="rounded-lg bg-[hsl(var(--muted))] p-2.5 text-xs text-muted-foreground leading-relaxed border border-border">
                {contact.note}
              </div>
            ) : (
              <div className="rounded-lg bg-[hsl(var(--muted))]/30 p-2.5 text-[11px] text-muted-foreground text-center border border-dashed border-border">
                Sin notas
              </div>
            )}
            <button className="mt-2 w-full text-[11px] text-muted-foreground hover:text-foreground border border-dashed border-border/60 rounded-lg py-1.5 transition hover:border-foreground/30">
              {contact.note ? 'Editar nota' : '+ Agregar nota'}
            </button>
          </div>

          {/* Etiquetas */}
          <div className="p-4">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              <Tag className="size-3" />
              Etiquetas
            </div>
            <div className="flex flex-wrap gap-1.5">
              {contact.tags.map((t) => (
                <div key={t} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--muted))] border border-border text-[11px] text-foreground">
                  {t}
                  <button className="text-muted-foreground hover:text-red-400 transition">
                    <X className="size-2.5" />
                  </button>
                </div>
              ))}
              <button className="px-2 py-0.5 rounded-full border border-dashed border-border text-[11px] text-muted-foreground hover:text-foreground hover:border-foreground/30 transition">
                + Agregar
              </button>
            </div>
          </div>

          {/* Historial */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-1.5 mb-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              <Clock className="size-3" />
              Actividad
            </div>
            <div className="space-y-2">
              {[
                { text: 'Primer contacto vía Instagram', time: 'hace 2 días' },
                { text: 'Consultó sobre Remera Oversize', time: 'hace 2 días' },
                { text: 'Retomó conversación hoy', time: 'hace 3 horas' },
              ].map((ev, i) => (
                <div key={i} className="flex items-start gap-2 text-[10px]">
                  <div className="mt-1 size-1.5 rounded-full bg-muted-foreground shrink-0" />
                  <div>
                    <p className="text-muted-foreground">{ev.text}</p>
                    <p className="text-muted-foreground">{ev.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
